import React, { useEffect, useRef, useState } from 'react';
import './dashboard-page.scss';
import { NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import ExportData from './ExportData';
import BlockHeader from '../../../components/layout/BlockHeader';
import { Icons } from '../../../components/base/BootstrapComponents/BootstrapComponents';
import CameraBlock from './CameraBlock';
import Grid from '../../../components/base/Grid/Grid';
import { responseLogWithNotification, useUpdatePeriod } from '../../../utils/utils';
import {
  loadDayStat, loadConfiguredViolations, loadExportStatus, loadNodeState,
} from '../actions';
import { getSystemData } from '../../System/reducer';
import { getDashboardData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import {
  dateTimeFormatter, regNumberFormatter, timeFormatter, tooltipFormatter,
} from '../../../utils/formatters';
import ConfiguredViolations from './ConfiguredViolations';
import SessionGrid from './SessionGrid';
import SessionViewModal from '../../Session/components/SessionViewModal';
import { loadDetectionList } from '../../Detection/actions';
import { getDetectionData } from '../../Detection/reducer';
import { getCamerasData } from '../../Settings/reducer';
import { getAuthorizedData } from '../../Authorized/reducer';

export default function DashboardPage() {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [currentTimeOut, setCurrentTimeOut] = useState(null);
  const [showSession, setShowSession] = useState(null);
  const [showConfiguredViolations, setShowConfiguredViolations] = useState(false);
  const { updatePeriod } = useSelector(getSystemData);
  const { loggedIn, token } = useSelector(getAuthorizedData);
  const intl = useIntl();
  const { formatMessage } = intl;
  const {
    loadingDayStat,
    dayStat,
    loadingLastSession,
    lastSession,
    loadingConfiguredViolations,
    configuredViolations = [],
    loadingCreateScreenshot,
    exportStatus,
    loadingExportStatus,
    loadingAllNodeEvent,
  } = useSelector(getDashboardData);
  const { list = [] } = useSelector(getDetectionData);
  const { loadingCameraList, cameraList = [] } = useSelector(getCamerasData);

  const loadData = () => {
    const p1 = dispatch(loadDayStat()).catch(responseLogWithNotification);
    // const p2 = dispatch(loadLastSession()).catch(responseLogWithNotification);
    const p2 = dispatch(loadDetectionList({ limit: 20, reverse_order: true })).catch(responseLogWithNotification);
    const p3 = dispatch(loadConfiguredViolations()).catch(responseLogWithNotification);
    const p4 = dispatch(loadExportStatus()).catch(responseLogWithNotification);
    const p5 = loggedIn && token
      ? dispatch(loadNodeState()).catch(responseLogWithNotification)
      : Promise.resolve();
    if (currentTimeOut) {
      clearTimeout(currentTimeOut);
    }
    return Promise.all([p1, p2, p3, p4, p5]);
  };

  const violationsColumns = [
    {
      dataField: 'violation_time',
      text: formatMessage({ id: 'time' }),
      disableSort: true,
      hidden: true,
      dataClassName: 'align-middle',
      headerClassName: 'column-0',
      formatter: timeFormatter,
    },
    {
      dataField: 'camera_id',
      text: formatMessage({ id: 'camera' }),
      disableSort: true,
      dataClassName: 'align-middle',
      headerClassName: 'column-1',
      formatter: tooltipFormatter,
    },
    {
      dataField: 'lp_text',
      text: formatMessage({ id: 'reg_number.short' }),
      formatter: regNumberFormatter,
      disableSort: true,
      dataClassName: 'align-middle',
      headerClassName: 'column-2',
    },
    {
      dataField: 'violation_description',
      text: formatMessage({ id: 'violation' }),
      disableSort: true,
      dataClassName: 'align-middle data-column-3',
      headerClassName: 'column-3',
      formatter: tooltipFormatter,
    },
  ];

  const configuredViolationsColumns = [
    {
      dataField: 'violation_description',
      text: formatMessage({ id: 'violation' }),
      disableSort: true,
      hidden: true,
      dataClassName: 'align-middle data-column-3',
      headerClassName: 'column-0',
      formatter: tooltipFormatter,
    },
    {
      dataField: 'violation_count',
      text: formatMessage({ id: 'count' }),
      disableSort: true,
      dataClassName: 'align-middle',
      headerClassName: 'column-1',
    },
  ];

  const cameraFormatter = id => {
    const camera = cameraList.find(f => f.id === id);
    return camera && camera.name ? camera.name : id;
  };

  const detectionColumns = [
    {
      dataField: 'detection_time',
      text: formatMessage({ id: 'car.detection.time' }),
      formatter: dateTimeFormatter,
      headerClassName: 'date-column',
      disableSort: true,
    },
    {
      dataField: 'lp_text',
      text: formatMessage({ id: 'reg_number.short' }),
      formatter: regNumberFormatter,
      headerClassName: 'reg-number-column',
      disableSort: true,
    },
    {
      dataField: 'camera_id',
      text: formatMessage({ id: 'camera' }),
      formatter: cameraFormatter,
      headerClassName: 'camera-column',
      disableSort: true,
    },
    {
      dataField: 'zone_name',
      text: formatMessage({ id: 'zone' }),
      formatter: tooltipFormatter,
      headerClassName: 'zone-column',
      disableSort: true,
    },
    {
      dataField: 'tour_id',
      text: formatMessage({ id: 'tour' }),
      formatter: tooltipFormatter,
      headerClassName: 'tour-column',
      disableSort: true,
    },
  ];

  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);

  const statColumns = [
    {
      dataField: 'name',
      text: formatMessage({ id: 'general.indicators' }),
      disableSort: true,
      hidden: true,
      dataClassName: 'align-middle data-column-3',
      formatter: name => tooltipFormatter(formatMessage({ id: name })),
      headerClassName: 'column-0',
    },
    {
      dataField: 'count',
      text: formatMessage({ id: 'count' }),
      disableSort: true,
      dataClassName: 'align-middle',
      headerClassName: 'column-1',
    },
  ];

  return (
    <div className="page dashboard-page">
      <Loader loading={loadingDayStat || loadingLastSession || loadingConfiguredViolations || loadingCameraList || loadingExportStatus || loadingCreateScreenshot || loadingAllNodeEvent} />
      <div className="page-title">
        <div className="title-text">{formatMessage({ id: 'menu.dashboard' })}</div>
        <ExportData data={exportStatus} />
      </div>
      <div className="page-content-wrapper">
        <div className="page-content" ref={blockRef}>
          <div className="dashboard-left-column frame">
            <div className="camera-block-wrapper">
              <CameraBlock blockRef={blockRef} />
            </div>
            <div className="stat-and-fixation-row">
              <div className="stat-block">
                <BlockHeader text={formatMessage({ id: 'day.stat' })} />
                <div className="grid-wrapper">
                  <div className="custom-scroll-theme sticky-table-wrapper">
                    <Grid columns={statColumns} data={dayStat} />
                  </div>
                </div>
              </div>
              <div className="fixation-block">
                <BlockHeader
                  text={formatMessage({ id: 'configured.violation' })}
                  content={(
                    <span onClick={() => { setShowConfiguredViolations(!showConfiguredViolations); }} className={classNames('link-text', { disabled: !configuredViolations.length })}>
                      {formatMessage({ id: 'show.all' })}
                      <Icons
                        width={24}
                        height={24}
                        customIcon
                        icon="arrow-right"
                        className="button-icons"
                      />
                    </span>
                  )}
                />
                <div className="grid-wrapper">
                  <div className="custom-scroll-theme sticky-table-wrapper">
                    <Grid columns={configuredViolationsColumns} data={configuredViolations} emptyText={formatMessage({ id: 'configured.violation.not.found' })} />
                  </div>
                </div>
                {showConfiguredViolations ? <ConfiguredViolations handleClose={() => { setShowConfiguredViolations(!showConfiguredViolations); }} columns={configuredViolationsColumns} data={configuredViolations} /> : null}
              </div>
            </div>
          </div>
          <div className="dashboard-right-column frame frame-padding">
            <BlockHeader
              text={formatMessage({ id: 'last.detection' })}
              content={(
                // <NavLink to="/session" className={classNames('link-text', { disabled: !lastSession.length })}>{formatMessage({ id: 'show.all' })} <Icons
                <NavLink to="/detection" className={classNames('link-text', { disabled: !list.length })}>{formatMessage({ id: 'show.all' })} <Icons
                  width={24}
                  height={24}
                  customIcon
                  icon="arrow-right"
                  className="button-icons"
                />
                </NavLink>
              )}
            />
            <div className="grid-wrapper">
              <div className="custom-scroll-theme sticky-table-wrapper">
                {/* <SessionGrid columns={violationsColumns} data={list} onClickByRow={item => setShowSession(item)} /> */}
                <SessionGrid columns={detectionColumns} data={list} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSession ? <SessionViewModal handleClose={() => setShowSession(null)} session={showSession} /> : null}
    </div>
  );
}
